import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { UpgradePlanModal } from 'components/Modals';
import { useCallback, useRef, useState } from 'react';
import useRegionStore from 'store/useRegionStore';
import useUserStore from 'store/useUserStore';
import { DEFAULT_REGION, Region } from 'types/Region';
import { containsWithoutSpecialChars } from 'util/containsWithoutSpecialChars';
import DefaultDropDown from './DefaultDropDown';

interface StateFilterProps {
    lock?: boolean;
    placeholder?: string;
    region: Region;
    selectedStates?: string[];
    onChanged?: (states: string[]) => void;
}

const StateFilter = ({ lock = false, selectedStates = [], onChanged = () => {}, region = DEFAULT_REGION, placeholder = 'State' }: StateFilterProps) => {
    const [showUpgradePlanModal, setShowUpgradePlanModal] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const childRef = useRef<{ closePopup: () => void }>(null);
    const { getStates, getNonStates, getFilteredStates } = useRegionStore();
    const { isPremiumUser } = useUserStore();

    // Toggle checkbox selection for a state
    const toggleStateSelection = useCallback(
        (item: string) => {
            let newSelection: string[];
            if (selectedStates.includes(item)) {
                newSelection = selectedStates.filter((state) => state !== item);
            } else {
                newSelection = [...selectedStates, item];
            }
            onChanged(newSelection);
        },
        [selectedStates, onChanged],
    );

    const onSearchTermChanged = (text: string) => setSearchTerm(text);
    const onClearClicked = () => onChanged([]);
    const onShowModal = () => {
        if (childRef.current) {
            childRef.current.closePopup();
        }
        if (!isPremiumUser()) setShowUpgradePlanModal(true);
    };

    const filteredStates = getFilteredStates(searchTerm);
    const states = getStates(lock, region);
    const nonStates = getNonStates(region);

    // Remove any states from nonStates that exist in states array
    const filteredNonStates = nonStates.filter((state) => !states.some((s) => s.State === state.State));

    const titleContent = selectedStates.length === 0 ? placeholder : <div className="truncate">{selectedStates.join(', ')}</div>;

    return (
        <>
            <DefaultDropDown
                ref={childRef}
                Title={<div className="truncate">{titleContent}</div>}
                IsEmpty={selectedStates.length === 0}
                onClearClicked={onClearClicked}
            >
                {/* Search Input */}
                <div className="px-[10px] py-[5px]">
                    <div className="rounded-[5px] border border-my-gray border-opacity-60 flex items-center justify-between gap-[5px] px-[10px] py-[5px]">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => onSearchTermChanged(e.target.value)}
                            className="flex-grow focus:outline-none w-full"
                        />
                        <SearchOutlinedIcon />
                    </div>
                </div>
                <div className="max-h-[300px] overflow-y-scroll">
                    {/* When no region is set, show filtered states */}
                    {region.name === '' &&
                        filteredStates
                            .filter((state) => !filteredNonStates.some((ns) => ns.State === state.title))
                            .map((state, index) =>
                                lock && state.lock ? (
                                    <label key={index} className="flex items-center gap-2 p-2 cursor-pointer opacity-50" onClick={onShowModal}>
                                        <input type="checkbox" checked={selectedStates.includes(state.title)} disabled />
                                        <span>{state.title}</span>
                                    </label>
                                ) : (
                                    <label key={index} className="flex items-center gap-2 p-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedStates.includes(state.title)}
                                            onChange={() => toggleStateSelection(state.title)}
                                        />
                                        <span>{state.title}</span>
                                    </label>
                                ),
                            )}

                    {/* Render states from getStates */}
                    {states
                        .filter((state) => containsWithoutSpecialChars(state.State, searchTerm))
                        .map((state, index) => (
                            <label key={index} className="flex items-center gap-2 p-2 cursor-pointer">
                                <input type="checkbox" checked={selectedStates.includes(state.State)} onChange={() => toggleStateSelection(state.State)} />
                                <span>{state.State}</span>
                            </label>
                        ))}

                    {/* For locked items (nonStates) */}
                    {lock &&
                        filteredNonStates
                            .filter((state) => containsWithoutSpecialChars(state.State, searchTerm))
                            .map((state, index) => (
                                <label key={index} className="flex items-center gap-2 p-2 cursor-pointer opacity-50" onClick={onShowModal}>
                                    <input type="checkbox" checked={selectedStates.includes(state.State)} disabled />
                                    <span>{state.State}</span>
                                </label>
                            ))}
                </div>
            </DefaultDropDown>

            <UpgradePlanModal visible={showUpgradePlanModal} onClose={() => setShowUpgradePlanModal(false)} />
        </>
    );
};

export default StateFilter;
